import React, { useState, useContext, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from 'react-i18next';
import './style.css'
import '../../styles/page-titles.css'
import styled from 'styled-components';
import 'bootstrap/dist/css/bootstrap.min.css';
import { SidebarContext } from "../../AppRoutes";
import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import Modal from "react-modal";
import { FaMoneyBillWave } from "react-icons/fa";

import moment from 'moment'

import regionConfig from "../../config/regionConfig";

import { deleteInstanceAdmin, getEmpresasAdmin, createInstanceAdmin, deleteEmpresaCompleta, toggleEmpresaAtiva, getFaturasAdmin } from "../../services/api";
import { isFeatureEnabled } from '../../hooks/useFeatureFlag';

import PermissionGate from "../../services/PermissionGate";
import ModalSubscriptionAdmin from './ModalSubscriptionAdmin';

import ConfirmDialog from "../../components/ConfirmDialog";

import * as FiIcons from 'react-icons/fi'
import * as AiIcons from 'react-icons/ai'
import * as SlIcons from 'react-icons/sl'
import * as FaIcons from 'react-icons/fa'

/* =========================================================================
   DESIGN TOKENS — paleta refinada (SaaS), sem gradientes pesados.
   Acento de marca usado com parcimônia; bordas em fio de cabelo; tipografia
   com hierarquia forte. IDs/CNPJ em monoespaçada para leitura técnica.
   ========================================================================= */
const T = {
    canvas: '#f5f6f8',
    surface: '#ffffff',
    border: '#e7e9ee',
    borderStrong: '#dcdfe6',
    ink: '#161b26',
    inkSoft: '#5b6573',
    inkFaint: '#8a93a3',
    accent: '#2f7fd1',
    accentSoft: 'rgba(47, 127, 209, 0.10)',
    okBg: '#e7f6ee', okInk: '#0f7a43',
    badBg: '#fdecec', badInk: '#c43d3d',
    warnBg: '#fdf0dc', warnInk: '#a86310',
    neutralBg: '#eef1f5', neutralInk: '#5b6573',
    danger: '#d6453f',
    mono: "'SFMono-Regular', 'JetBrains Mono', 'Roboto Mono', ui-monospace, Menlo, Consolas, monospace",
};

const statusMap = {
    PENDING: "Pendente",
    PAID: "Paga",
    CANCELED: "Cancelada",
    IN_ANALYSIS: "Em Análise",
    DRAFT: "Rascunho",
    PARTIALLY_PAID: "Parcialmente Paga",
    REFUNDED: "Reembolsada",
    EXPIRED: "Expirada",
    IN_PROTEST: "Em Protesto",
    CHARGEBACK: "Contestada",
    EXTERNALLY_PAID: "Paga Externamente",
    RECEIVED: "Paga",
    CONFIRMED: "Confirmada",
    OVERDUE: "Atrasada",
    RECEIVED_IN_CASH: "Recebida em Dinheiro",
    REFUND_REQUESTED: "Reembolso Solicitado",
    REFUND_IN_PROGRESS: "Reembolso em Andamento",
    CHARGEBACK_REQUESTED: "Contestação Solicitada",
    CHARGEBACK_DISPUTE: "Disputa de Contestação",
    AWAITING_CHARGEBACK_REVERSAL: "Aguardando Reversão de Contestação",
    DUNNING_REQUESTED: "Cobrança Judicial Solicitada",
    DUNNING_RECEIVED: "Cobrança Judicial Recebida",
    AWAITING_RISK_ANALYSIS: "Aguardando Análise de Risco",
};

// Classifica o status do Asaas num "tom" visual pra pílula de status
const invoiceTone = (status) => {
    if (['RECEIVED', 'CONFIRMED', 'RECEIVED_IN_CASH', 'EXTERNALLY_PAID'].includes(status)) return 'ok';
    if (status === 'OVERDUE') return 'bad';
    if (['PENDING', 'AWAITING_RISK_ANALYSIS', 'DUNNING_REQUESTED', 'DUNNING_RECEIVED'].includes(status)) return 'warn';
    return 'neutral';
};

const InvoicesCard = styled.div`
    width: 600px;
    max-width: calc(100vw - 32px);
    max-height: calc(100vh - 64px);
    display: flex;
    flex-direction: column;
    background: ${T.surface};
    border: 1px solid ${T.border};
    border-radius: 16px;
    box-shadow: 0 24px 60px rgba(16, 24, 40, 0.24);
    overflow: hidden;
    font-family: inherit;

    .inv-header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 16px;
        padding: 20px 22px 16px;
        border-bottom: 1px solid ${T.border};

        .inv-title { font-size: 18px; font-weight: 700; color: ${T.ink}; letter-spacing: -0.01em; margin: 0; }
        .inv-subtitle { font-size: 13px; color: ${T.inkSoft}; margin: 3px 0 0; }
        .inv-close {
            flex-shrink: 0;
            width: 32px; height: 32px;
            display: flex; align-items: center; justify-content: center;
            border: none; border-radius: 8px; background: transparent;
            color: ${T.inkFaint}; font-size: 19px; cursor: pointer;
            transition: all 0.15s ease;
            &:hover { background: ${T.neutralBg}; color: ${T.ink}; }
        }
    }

    .inv-body { overflow-y: auto; padding: 6px 14px; }

    .inv-empty {
        padding: 48px 24px;
        text-align: center;
        color: ${T.inkFaint};
        font-size: 14px;
    }

    .inv-spinner {
        font-size: 20px;
        color: ${T.accent};
        vertical-align: middle;
        margin-right: 6px;
        animation: inv-spin 0.9s linear infinite;
    }
    @keyframes inv-spin { to { transform: rotate(360deg); } }

    .inv-row {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 13px 8px;
        flex-wrap: wrap;
        &:not(:last-child) { border-bottom: 1px solid ${T.border}; }

        .inv-date { font-family: ${T.mono}; font-size: 12.5px; color: ${T.inkSoft}; white-space: nowrap; }
        .inv-value { margin-left: auto; font-weight: 700; color: ${T.ink}; font-size: 14px; white-space: nowrap; }
        .inv-pay-na { color: ${T.inkFaint}; }
    }

    .inv-pill {
        display: inline-flex; align-items: center;
        padding: 3px 10px; border-radius: 999px;
        font-size: 11.5px; font-weight: 600; white-space: nowrap;
        &.ok { background: ${T.okBg}; color: ${T.okInk}; }
        &.bad { background: ${T.badBg}; color: ${T.badInk}; }
        &.warn { background: ${T.warnBg}; color: ${T.warnInk}; }
        &.neutral { background: ${T.neutralBg}; color: ${T.neutralInk}; }
    }

    .inv-pay-btn {
        display: inline-flex; align-items: center; gap: 6px;
        padding: 7px 13px; border-radius: 8px;
        background: ${T.accent}; color: #fff;
        font-size: 13px; font-weight: 600; text-decoration: none;
        white-space: nowrap; transition: background 0.15s ease;
        &:hover { background: #2a72bd; }
    }

    .inv-footer {
        padding: 14px 22px;
        border-top: 1px solid ${T.border};
        display: flex; justify-content: flex-end;

        .inv-close-btn {
            padding: 9px 18px; border-radius: 9px;
            border: 1px solid ${T.border}; background: ${T.surface};
            color: ${T.inkSoft}; font-size: 14px; font-weight: 600; cursor: pointer;
            transition: all 0.15s ease;
            &:hover { border-color: ${T.accent}; color: ${T.accent}; background: ${T.accentSoft}; }
        }
    }
`;

const InvoicesModal = ({ invoices, loading, isOpen, onClose, t }) => {
    const fmt = (v) => (v || 0).toLocaleString(
        regionConfig.currency.locale,
        { style: 'currency', currency: regionConfig.currency.code }
    );
    const count = invoices?.length || 0;

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            contentLabel={t('common:adminEmpresas.invoices.title')}
            ariaHideApp={false}
            style={{
                overlay: {
                    backgroundColor: 'rgba(16, 24, 40, 0.55)',
                    zIndex: 3000,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 16,
                },
                content: {
                    position: 'static',
                    inset: 'auto',
                    border: 'none',
                    background: 'transparent',
                    padding: 0,
                    overflow: 'visible',
                    borderRadius: 0,
                    width: 'auto',
                    maxWidth: '100%',
                },
            }}
        >
            <InvoicesCard>
                <div className="inv-header">
                    <div>
                        <h2 className="inv-title">{t('common:adminEmpresas.invoices.title')}</h2>
                        <p className="inv-subtitle">
                            {loading
                                ? t('common:adminEmpresas.invoices.loading')
                                : `${count} ${count === 1 ? t('common:adminEmpresas.invoices.singular') : t('common:adminEmpresas.invoices.plural')}`}
                        </p>
                    </div>
                    <button className="inv-close" onClick={onClose} aria-label={t('common:buttons.close')}>
                        <FiIcons.FiX />
                    </button>
                </div>

                <div className="inv-body">
                    {loading ? (
                        <div className="inv-empty">
                            <AiIcons.AiOutlineLoading3Quarters className="inv-spinner" /> {t('common:adminEmpresas.invoices.loading')}
                        </div>
                    ) : count === 0 ? (
                        <div className="inv-empty">{t('common:adminEmpresas.invoices.notGenerated')}</div>
                    ) : (
                        invoices.map((invoice, index) => {
                            const isPayable = invoice?.status === 'OVERDUE' || invoice?.status === 'PENDING';
                            const value = isPayable
                                ? (invoice?.value || 0) - (invoice?.discount?.value || 0)
                                : (invoice?.value || 0);
                            return (
                                <div className="inv-row" key={index}>
                                    <span className="inv-date">{moment(invoice?.dueDate).format('DD/MM/YYYY')}</span>
                                    <span className={`inv-pill ${invoiceTone(invoice?.status)}`}>
                                        {statusMap[invoice?.status] || t('common:adminEmpresas.invoices.unknownStatus')}
                                    </span>
                                    <span className="inv-value">{fmt(value)}</span>
                                    {isPayable ? (
                                        <a
                                            className="inv-pay-btn"
                                            href={invoice?.invoiceUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <FaMoneyBillWave /> {t('common:adminEmpresas.invoices.pay')}
                                        </a>
                                    ) : (
                                        <span className="inv-pay-na">—</span>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>

                <div className="inv-footer">
                    <button className="inv-close-btn" onClick={onClose}>{t('common:buttons.close')}</button>
                </div>
            </InvoicesCard>
        </Modal>
    );
};

const DeleteMasterModal = ({ isOpen, onClose, onConfirm, empresaData, isLoading, t }) => {
    const [usuario, setUsuario] = useState('');
    const [senha, setSenha] = useState('');
    const [confirmarNome, setConfirmarNome] = useState('');

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!usuario.trim() || !senha.trim()) {
            toast.error(t('common:adminEmpresas.deleteMaster.userPasswordRequired'));
            return;
        }

        if (confirmarNome.toLowerCase().trim() !== empresaData?.name?.toLowerCase().trim()) {
            toast.error(t('common:adminEmpresas.deleteMaster.companyNameMismatch'));
            return;
        }

        onConfirm(usuario, senha);
    };

    const handleClose = () => {
        setUsuario('');
        setSenha('');
        setConfirmarNome('');
        onClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={handleClose}
            contentLabel={t('common:adminEmpresas.deleteMaster.contentLabel')}
            ariaHideApp={false}
            className="delete-master-modal"
            overlayClassName="delete-master-overlay"
        >
            <div className="delete-master-content">
                <div className="delete-master-header">
                    <h2>{t('common:adminEmpresas.deleteMaster.title')}</h2>
                </div>

                <div className="delete-master-warning">
                    <p><strong>{t('common:adminEmpresas.deleteMaster.warning')}</strong></p>
                    <ul>
                        <li>{t('common:adminEmpresas.deleteMaster.itemCompany')} <strong>{empresaData?.name}</strong></li>
                        <li>{t('common:adminEmpresas.deleteMaster.itemClients')}</li>
                        <li>{t('common:adminEmpresas.deleteMaster.itemOrders')}</li>
                        <li>{t('common:adminEmpresas.deleteMaster.itemMenu')}</li>
                        <li>{t('common:adminEmpresas.deleteMaster.itemUsers')}</li>
                        <li>{t('common:adminEmpresas.deleteMaster.itemCashRegister')}</li>
                        <li>{t('common:adminEmpresas.deleteMaster.itemWhatsapp')}</li>
                    </ul>
                    <p className="irreversible-warning">
                        <FiIcons.FiAlertCircle /> <strong>{t('common:adminEmpresas.deleteMaster.irreversibleWarning')}</strong>
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="delete-master-form">
                    <div className="form-group">
                        <label>{t('common:adminEmpresas.deleteMaster.masterUser')}</label>
                        <input
                            type="text"
                            value={usuario}
                            onChange={(e) => setUsuario(e.target.value)}
                            className="form-control"
                            placeholder={t('common:adminEmpresas.deleteMaster.masterUserPlaceholder')}
                            disabled={isLoading}
                        />
                    </div>

                    <div className="form-group">
                        <label>{t('common:adminEmpresas.deleteMaster.masterPassword')}</label>
                        <input
                            type="password"
                            value={senha}
                            onChange={(e) => setSenha(e.target.value)}
                            className="form-control"
                            placeholder={t('common:adminEmpresas.deleteMaster.masterPasswordPlaceholder')}
                            disabled={isLoading}
                        />
                    </div>

                    <div className="form-group">
                        <label>{t('common:adminEmpresas.deleteMaster.confirmLabel')}</label>
                        <input
                            type="text"
                            value={confirmarNome}
                            onChange={(e) => setConfirmarNome(e.target.value)}
                            className="form-control"
                            placeholder={`${t('common:adminEmpresas.deleteMaster.typeExactly')} ${empresaData?.name}`}
                            disabled={isLoading}
                        />
                        <small className="text-muted">{t('common:adminEmpresas.deleteMaster.typeExactly')} <strong>{empresaData?.name}</strong></small>
                    </div>

                    <div className="delete-master-buttons">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="btn-cancel"
                            disabled={isLoading}
                        >
                            <FiIcons.FiX /> {t('common:buttons.cancel')}
                        </button>
                        <button
                            type="submit"
                            className="btn-delete-master"
                            disabled={isLoading || !usuario || !senha || !confirmarNome}
                        >
                            {isLoading ? (
                                <>
                                    <AiIcons.AiOutlineLoading3Quarters className="spinning" />
                                    {t('common:adminEmpresas.deleteMaster.deleting')}
                                </>
                            ) : (
                                <>
                                    <FiIcons.FiTrash2 /> {t('common:adminEmpresas.deleteMaster.deletePermanently')}
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </Modal>
    );
};

const AdminEmpresasContainer = styled.div`
    margin-left: ${({ sidebar }) => (sidebar ? '250px' : '100px')};
    min-height: 100vh;
    width: auto;
    transition: margin-left 200ms ease;
    background: ${T.canvas};
    padding: 32px 28px 48px;

    @media (max-width: 880px) {
        margin-left: 0;
        padding: 20px 16px 40px;
    }
`;

const AdminEmpresasContent = styled.div`
    width: 100%;
    margin: 0 auto;
`;

const AdminEmpresasHeader = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 14px;
    margin-bottom: 26px;

    .admin-empresas-header-accent {
        flex-shrink: 0;
        width: 4px;
        align-self: stretch;
        min-height: 44px;
        border-radius: 4px;
        background: ${T.accent};
        margin-top: 2px;
    }

    .admin-empresas-title {
        color: ${T.ink};
        font-size: 26px;
        font-weight: 700;
        letter-spacing: -0.02em;
        line-height: 1.15;
        margin: 0 0 4px 0;
    }

    .admin-empresas-subtitle {
        color: ${T.inkSoft};
        font-size: 14.5px;
        margin: 0;
        font-weight: 400;
    }

    @media (max-width: 768px) {
        .admin-empresas-title { font-size: 22px; }
        .admin-empresas-subtitle { font-size: 13px; }
    }
`;

const AdminEmpresasControls = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 18px;

    .admin-empresas-controls-left {
        display: flex;
        align-items: center;
        gap: 12px;
        flex: 1;
        flex-wrap: wrap;
    }

    .admin-empresas-filter {
        position: relative;
        display: flex;
        align-items: center;

        .admin-empresas-filter-icon {
            position: absolute;
            left: 13px;
            color: ${T.inkFaint};
            font-size: 15px;
            pointer-events: none;
        }

        .admin-empresas-filter-select {
            height: 44px;
            padding: 0 14px 0 34px;
            border: 1px solid ${T.border};
            border-radius: 10px;
            background: ${T.surface};
            color: ${T.ink};
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            transition: border-color 0.15s ease, box-shadow 0.15s ease;

            &:focus {
                outline: none;
                border-color: ${T.accent};
                box-shadow: 0 0 0 3px ${T.accentSoft};
            }
        }
    }

    .admin-empresas-daterange {
        display: flex;
        align-items: center;
        gap: 6px;
        height: 44px;
        padding: 0 12px;
        border: 1px solid ${T.border};
        border-radius: 10px;
        background: ${T.surface};

        .admin-empresas-daterange-icon { color: ${T.inkFaint}; font-size: 15px; flex-shrink: 0; }
        .admin-empresas-daterange-sep { color: ${T.inkFaint}; }
        .admin-empresas-date-input {
            border: none;
            background: transparent;
            color: ${T.ink};
            font-size: 13px;
            font-family: inherit;
            cursor: pointer;
            min-width: 0;
            &:focus { outline: none; }
        }
    }

    .admin-empresas-search-container {
        position: relative;
        flex: 1;
        max-width: 440px;

        .admin-empresas-search-input {
            width: 100%;
            height: 44px;
            padding: 0 16px 0 44px;
            border: 1px solid ${T.border};
            border-radius: 10px;
            font-size: 14.5px;
            font-weight: 400;
            color: ${T.ink};
            background: ${T.surface};
            transition: border-color 0.15s ease, box-shadow 0.15s ease;

            &:focus {
                outline: none;
                border-color: ${T.accent};
                box-shadow: 0 0 0 3px ${T.accentSoft};
            }

            &::placeholder { color: ${T.inkFaint}; }
        }

        .admin-empresas-search-icon {
            position: absolute;
            left: 15px;
            top: 50%;
            transform: translateY(-50%);
            color: ${T.inkFaint};
            font-size: 17px;
        }
    }

    .admin-empresas-refresh-btn {
        height: 44px;
        padding: 0 18px;
        background: ${T.surface};
        color: ${T.inkSoft};
        border: 1px solid ${T.border};
        border-radius: 10px;
        font-size: 14px;
        font-weight: 600;
        display: flex;
        align-items: center;
        gap: 8px;
        cursor: pointer;
        transition: all 0.15s ease;

        svg { transition: transform 0.4s ease; }

        &:hover:not(:disabled) {
            border-color: ${T.accent};
            color: ${T.accent};
            background: ${T.accentSoft};
            svg { transform: rotate(180deg); }
        }

        &:disabled { opacity: 0.6; cursor: not-allowed; }
    }

    @media (max-width: 768px) {
        .admin-empresas-controls-left { width: 100%; }
        .admin-empresas-search-container { max-width: 100%; flex: 1 1 100%; }
        .admin-empresas-filter,
        .admin-empresas-filter .admin-empresas-filter-select,
        .admin-empresas-daterange { width: 100%; }
        .admin-empresas-refresh-btn { width: 100%; justify-content: center; }
    }
`;

const AdminEmpresasTable = styled.div`
    background: ${T.surface};
    border-radius: 14px;
    border: 1px solid ${T.border};
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04), 0 4px 16px rgba(16, 24, 40, 0.04);
    overflow: hidden;
    position: relative;
    width: 100%;

    .admin-empresas-table-overlay {
        position: absolute;
        inset: 0;
        background: rgba(255, 255, 255, 0.6);
        backdrop-filter: blur(1px);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 10;

        .admin-empresas-loading-spinner {
            color: ${T.accent};
            font-size: 34px;
            animation: spin 0.9s linear infinite;
        }

        @keyframes spin { to { transform: rotate(360deg); } }
    }

    .admin-empresas-table-wrapper {
        overflow-x: auto;
    }

    .admin-empresas-table {
        width: 100%;
        min-width: 100%;
        border-collapse: collapse;
        table-layout: auto;

        .admin-empresas-table-header {
            background: #fafbfc;
            border-bottom: 1px solid ${T.border};

            th {
                padding: 13px 14px;
                text-align: left;
                font-weight: 600;
                font-size: 11.5px;
                letter-spacing: 0.04em;
                text-transform: uppercase;
                color: ${T.inkFaint};
                white-space: nowrap;

                &:first-child { padding-left: 22px; }
                /* Coluna Ações: fixa à direita (sempre visível, mesmo com scroll horizontal) */
                &:last-child {
                    padding-right: 22px;
                    text-align: right;
                    position: sticky;
                    right: 0;
                    z-index: 2;
                    background: #fafbfc;
                    box-shadow: -8px 0 10px -8px rgba(16, 24, 40, 0.12);
                }
            }
        }

        .admin-empresas-table-body {
            tr {
                border-bottom: 1px solid ${T.border};
                transition: background-color 0.12s ease;

                &:hover { background: #fafbfc; }
                &:last-child { border-bottom: none; }

                td {
                    padding: 14px;
                    vertical-align: middle;
                    color: ${T.inkSoft};
                    font-size: 13.5px;

                    &:first-child {
                        padding-left: 22px;
                        font-weight: 600;
                        color: ${T.ink};
                    }

                    /* Coluna Ações: fixa à direita */
                    &:last-child {
                        padding-right: 22px;
                        text-align: right;
                        position: sticky;
                        right: 0;
                        z-index: 1;
                        background: ${T.surface};
                        box-shadow: -8px 0 10px -8px rgba(16, 24, 40, 0.12);
                    }
                }

                &:hover td:last-child { background: #fafbfc; }

                .admin-empresas-cell-email { color: ${T.inkSoft}; }

                .admin-empresas-cell-mono {
                    font-family: ${T.mono};
                    font-size: 12.5px;
                    color: ${T.inkSoft};
                    letter-spacing: -0.01em;
                }

                .admin-empresas-cell-instance {
                    font-family: ${T.mono};
                    font-size: 11.5px;
                    color: ${T.inkFaint};
                    max-width: 200px;
                    display: inline-block;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    vertical-align: middle;
                }
            }
        }
    }

    /* Pílulas de status (WhatsApp + Situação) */
    .admin-empresas-status,
    .admin-empresas-active-badge {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 4px 10px 4px 9px;
        border-radius: 999px;
        font-size: 12px;
        font-weight: 600;
        white-space: nowrap;
        line-height: 1.4;

        .dot {
            width: 6px;
            height: 6px;
            border-radius: 50%;
            background: currentColor;
        }
    }
    .admin-empresas-status.connected,
    .admin-empresas-active-badge.active { background: ${T.okBg}; color: ${T.okInk}; }
    .admin-empresas-status.disconnected { background: ${T.badBg}; color: ${T.badInk}; }
    .admin-empresas-status.unknown { background: ${T.neutralBg}; color: ${T.neutralInk}; }
    .admin-empresas-active-badge.inactive { background: ${T.warnBg}; color: ${T.warnInk}; }

    .admin-empresas-faturas-link {
        color: ${T.accent};
        font-weight: 600;
        cursor: pointer;
        text-decoration: none;
        &:hover { text-decoration: underline; }
    }
    .admin-empresas-faturas-empty { color: ${T.inkFaint}; font-size: 12.5px; }

    /* Gatilho do menu de ações (kebab) */
    .admin-empresas-actions-trigger {
        width: 34px;
        height: 34px;
        border: 1px solid transparent;
        border-radius: 8px;
        background: transparent;
        color: ${T.inkSoft};
        display: inline-flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        font-size: 18px;
        transition: all 0.15s ease;

        &:hover, &.is-open {
            background: ${T.accentSoft};
            color: ${T.accent};
            border-color: ${T.border};
        }
    }

    @media (max-width: 768px) {
        .admin-empresas-table {
            .admin-empresas-table-header th,
            .admin-empresas-table-body td {
                padding: 11px 9px;
                &:first-child { padding-left: 16px; }
                &:last-child { padding-right: 16px; }
            }
        }
    }

    @media (max-width: 480px) {
        .admin-empresas-table-wrapper {
            overflow-x: scroll;
            -webkit-overflow-scrolling: touch;
        }
        .admin-empresas-table {
            min-width: 860px;
            .admin-empresas-table-body td { font-size: 12.5px; }
        }
    }
`;

/* Popover do menu de ações — renderizado em document.body (escapa do overflow da tabela) */
const ActionMenuPortal = styled.div`
    position: fixed;
    z-index: 4000;
    min-width: 226px;
    background: ${T.surface};
    border: 1px solid ${T.border};
    border-radius: 12px;
    box-shadow: 0 14px 36px rgba(16, 24, 40, 0.16), 0 2px 6px rgba(16, 24, 40, 0.06);
    padding: 6px;
    animation: menu-pop 0.13s ease-out;
    transform-origin: top right;

    @keyframes menu-pop {
        from { opacity: 0; transform: translateY(-4px) scale(0.98); }
        to { opacity: 1; transform: translateY(0) scale(1); }
    }

    .menu-item {
        width: 100%;
        display: flex;
        align-items: center;
        gap: 11px;
        padding: 9px 11px;
        border: none;
        background: transparent;
        border-radius: 8px;
        font-size: 13.5px;
        font-weight: 500;
        color: ${T.ink};
        cursor: pointer;
        text-align: left;
        transition: background 0.12s ease, color 0.12s ease;

        svg { font-size: 15px; color: ${T.inkFaint}; flex-shrink: 0; transition: color 0.12s ease; }

        &:hover { background: ${T.accentSoft}; color: ${T.accent}; }
        &:hover svg { color: ${T.accent}; }

        &.success:hover { background: ${T.okBg}; color: ${T.okInk}; }
        &.success:hover svg { color: ${T.okInk}; }

        &.warn:hover { background: ${T.warnBg}; color: ${T.warnInk}; }
        &.warn:hover svg { color: ${T.warnInk}; }

        &.danger { color: ${T.danger}; }
        &.danger svg { color: ${T.danger}; }
        &.danger:hover { background: ${T.badBg}; }
    }

    .menu-divider {
        height: 1px;
        background: ${T.border};
        margin: 5px 8px;
    }
`;

const AdminEmpresasPagination = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 22px;
    background: #fafbfc;
    border-top: 1px solid ${T.border};

    .admin-empresas-pagination-info {
        color: ${T.inkSoft};
        font-size: 13px;
        font-weight: 400;

        .admin-empresas-total-highlight {
            color: ${T.ink};
            font-weight: 700;
        }
    }

    .admin-empresas-pagination-controls {
        display: flex;
        align-items: center;
        gap: 14px;

        .admin-empresas-page-size-selector {
            display: flex;
            align-items: center;
            gap: 8px;

            label { color: ${T.inkSoft}; font-size: 13px; font-weight: 400; }

            select {
                padding: 6px 10px;
                border: 1px solid ${T.border};
                border-radius: 8px;
                background: ${T.surface};
                font-size: 13px;
                color: ${T.ink};
                cursor: pointer;
                &:focus { outline: none; border-color: ${T.accent}; box-shadow: 0 0 0 3px ${T.accentSoft}; }
            }
        }

        .admin-empresas-page-navigation {
            display: flex;
            align-items: center;
            gap: 6px;

            .admin-empresas-page-btn {
                min-width: 34px;
                height: 34px;
                padding: 0 8px;
                border: 1px solid ${T.border};
                background: ${T.surface};
                border-radius: 8px;
                display: flex;
                align-items: center;
                justify-content: center;
                cursor: pointer;
                font-weight: 600;
                font-size: 13px;
                transition: all 0.15s ease;
                color: ${T.inkSoft};

                &:hover:not(:disabled):not(.active) {
                    border-color: ${T.accent};
                    color: ${T.accent};
                    background: ${T.accentSoft};
                }

                &:disabled { opacity: 0.45; cursor: not-allowed; }

                &.active {
                    background: ${T.accent};
                    border-color: ${T.accent};
                    color: #fff;
                }
            }
        }
    }

    @media (max-width: 768px) {
        flex-direction: column;
        gap: 14px;
        text-align: center;
        .admin-empresas-pagination-controls { flex-direction: column; gap: 14px; }
    }
`;

const AdminEmpresasLoading = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 90px 24px;

    .admin-empresas-loading-spinner {
        color: ${T.accent};
        font-size: 40px;
        animation: spin 0.9s linear infinite;
    }

    .admin-empresas-loading-text {
        color: ${T.inkSoft};
        font-size: 14.5px;
        font-weight: 500;
        margin-top: 14px;
    }

    @keyframes spin { to { transform: rotate(360deg); } }
`;

/* Badge de Situação (Ativa/Inativa) — distinto do badge de conexão WhatsApp */
const StatusBadge = ({ inativo, t }) => (
    <span className={`admin-empresas-active-badge ${inativo ? 'inactive' : 'active'}`}>
        <span className="dot" />
        {inativo
            ? t('common:adminEmpresas.status.inactive')
            : t('common:adminEmpresas.status.active')}
    </span>
);

/* Menu de ações consolidado (kebab + popover via portal).
   Substitui os botões empilhados. Fecha em: clique fora, Escape, scroll, seleção. */
const EmpresaActionsMenu = ({
    empresa, showSubscription,
    onViewInvoices, onManageSubscription, onCreateInstance, onDeleteInstance,
    onToggleActive, onDeleteMaster, t
}) => {
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState({ top: 0, left: 0 });
    const btnRef = useRef(null);
    const menuRef = useRef(null);

    const hasInstance = !!empresa.whatsapp?.id;
    const isInativo = !!empresa.inativo;
    const hasFaturas = !!empresa.hasFaturas;

    const updatePos = useCallback(() => {
        if (!btnRef.current) return;
        const r = btnRef.current.getBoundingClientRect();
        const menuW = 226;
        const menuH = menuRef.current?.offsetHeight || 300;
        let left = r.right - menuW;            // alinha pela borda direita do gatilho
        let top = r.bottom + 6;
        if (left < 8) left = 8;
        if (left + menuW > window.innerWidth - 8) left = window.innerWidth - menuW - 8;
        if (top + menuH > window.innerHeight - 8) top = r.top - menuH - 6; // vira pra cima
        if (top < 8) top = 8;
        setPos({ top, left });
    }, []);

    useEffect(() => {
        if (!open) return;
        updatePos();
        // segundo passo após render do menu (altura real)
        const raf = requestAnimationFrame(updatePos);
        const close = () => setOpen(false);
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        const onDown = (e) => {
            if (btnRef.current?.contains(e.target)) return;
            if (menuRef.current?.contains(e.target)) return;
            setOpen(false);
        };
        window.addEventListener('scroll', close, true);
        window.addEventListener('resize', updatePos);
        document.addEventListener('keydown', onKey);
        document.addEventListener('mousedown', onDown);
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener('scroll', close, true);
            window.removeEventListener('resize', updatePos);
            document.removeEventListener('keydown', onKey);
            document.removeEventListener('mousedown', onDown);
        };
    }, [open, updatePos]);

    const run = (fn) => () => { setOpen(false); fn?.(); };

    return (
        <>
            <button
                ref={btnRef}
                type="button"
                className={`admin-empresas-actions-trigger ${open ? 'is-open' : ''}`}
                onClick={() => setOpen((o) => !o)}
                title={t('common:adminEmpresas.actions.openMenu')}
                aria-haspopup="menu"
                aria-expanded={open}
            >
                <FiIcons.FiMoreVertical />
            </button>

            {open && createPortal(
                <ActionMenuPortal
                    ref={menuRef}
                    className="admin-empresas-action-menu"
                    style={{ top: pos.top, left: pos.left }}
                    role="menu"
                >
                    {hasFaturas && (
                        <button type="button" className="menu-item" role="menuitem" onClick={run(onViewInvoices)}>
                            <FaIcons.FaFileInvoiceDollar /> {t('common:adminEmpresas.invoices.viewInvoices')}
                        </button>
                    )}

                    {showSubscription && (
                        <button type="button" className="menu-item" role="menuitem" onClick={run(onManageSubscription)}>
                            <FaIcons.FaCrown /> {t('common:adminEmpresas.actions.manageSubscription')}
                        </button>
                    )}

                    {hasInstance ? (
                        <button type="button" className="menu-item" role="menuitem" onClick={run(onDeleteInstance)}>
                            <FiIcons.FiTrash2 /> {t('common:adminEmpresas.actions.deleteInstance')}
                        </button>
                    ) : (
                        <button type="button" className="menu-item" role="menuitem" onClick={run(onCreateInstance)}>
                            <FiIcons.FiPlusCircle /> {t('common:adminEmpresas.actions.createInstance')}
                        </button>
                    )}

                    <button
                        type="button"
                        className={`menu-item ${isInativo ? 'success' : 'warn'}`}
                        role="menuitem"
                        onClick={run(onToggleActive)}
                    >
                        {isInativo ? <FiIcons.FiCheckCircle /> : <FiIcons.FiSlash />}
                        {isInativo
                            ? t('common:adminEmpresas.actions.activate')
                            : t('common:adminEmpresas.actions.deactivate')}
                    </button>

                    <div className="menu-divider" />

                    <button type="button" className="menu-item danger" role="menuitem" onClick={run(onDeleteMaster)}>
                        <FaIcons.FaBomb /> {t('common:adminEmpresas.actions.deleteMasterComplete')}
                    </button>
                </ActionMenuPortal>,
                document.body
            )}
        </>
    );
};

const ListEmpresas = ({ list = [] }) => {
    const { t } = useTranslation(['common']);
    const { sidebar } = useContext(SidebarContext);

    const [empresas, setEmpresas] = useState([]); // Lista de empresas da página atual
    const [filter, setFilter] = useState('');
    const [situacaoFilter, setSituacaoFilter] = useState('ativa'); // Filtro de situação — padrão: empresas ativas
    const [whatsappFilter, setWhatsappFilter] = useState('todas'); // Conexão WhatsApp
    const [planoFilter, setPlanoFilter] = useState('todos');       // Status do plano
    const [dataInicio, setDataInicio] = useState('');              // Período de criação (de)
    const [dataFim, setDataFim] = useState('');                    // Período de criação (até)
    const [loading, setLoading] = useState(true); // Loading inicial (tela inteira)
    const [searching, setSearching] = useState(false); // Loading de busca (apenas tabela)
    const [refresh, setRefresh] = useState(false);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(50);
    const [total, setTotal] = useState(0); // Total do servidor
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [invoices, setInvoices] = useState([]);
    const [invoicesLoading, setInvoicesLoading] = useState(false);
    const [deleteMasterModalOpen, setDeleteMasterModalOpen] = useState(false);
    const [empresaToDelete, setEmpresaToDelete] = useState(null);
    const [deleteMasterLoading, setDeleteMasterLoading] = useState(false);

    const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
    const [selectedEmpresaForSub, setSelectedEmpresaForSub] = useState(null);

    // Ativar/Inativar empresa (confirm leve via ConfirmDialog)
    const [toggleConfirmOpen, setToggleConfirmOpen] = useState(false);
    const [empresaToToggle, setEmpresaToToggle] = useState(null);
    const [toggleLoading, setToggleLoading] = useState(false);

    const showSubscriptionColumn = !isFeatureEnabled('asaas');

    // Debounce para busca - evita requisições a cada tecla
    const [debouncedSearch, setDebouncedSearch] = useState('');

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(filter);
        }, 500); // 500ms de delay

        return () => clearTimeout(timer);
    }, [filter]);

    useEffect(() => {
        (async () => {
            try {
                // Se já carregou uma vez, usa searching (não esconde a tela inteira)
                if (empresas.length > 0 || !loading) {
                    setSearching(true);
                } else {
                    setLoading(true);
                }

                // V2: Com busca server-side
                const response = await getEmpresasAdmin(page, limit, debouncedSearch, {
                    situacao: situacaoFilter,
                    whatsapp: whatsappFilter,
                    plano: planoFilter,
                    dataInicio,
                    dataFim,
                });
                setEmpresas(response.data.empresas);
                setTotal(response.data.total);
            } catch (error) {
                process.env.REACT_APP_ISDEVELOP === 'true' && console.error("Erro ao carregar empresas:", error);
                toast(t('common:adminEmpresas.loadError'), { autoClose: 5000, type: "error" });
                setEmpresas([]);
            } finally {
                setLoading(false);
                setSearching(false);
                setRefresh(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refresh, page, limit, debouncedSearch, situacaoFilter, whatsappFilter, planoFilter, dataInicio, dataFim]);

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [shouldDelete, setShouldDelete] = useState(false);
    const [idToDelete, setIdToDelete] = useState('');
    const handleDelete = (idRecebido) => {
        // Abre Componente de Confirmação do delete
        setConfirmOpen(true)

        setIdToDelete(idRecebido)
        if (idRecebido) {
            setShouldDelete(true);
        }
    }

    const deleteReg = async () => {
        if (shouldDelete) {
            setLoading(true);
            try {
                const cast = await deleteInstanceAdmin(idToDelete);
                if (cast.status === 201) {
                    toast(cast.data.msg, { autoClose: 5000, type: "success" });
                    setRefresh(true);
                } else {
                    toast(cast.data.msg, { autoClose: 5000, type: "error" });
                }
            } catch (err) {
                toast(err.response?.data?.msg || "Erro ao deletar instância", { autoClose: 5000, type: "error" });
            } finally {
                setLoading(false);
                setConfirmOpen(false);
                setShouldDelete(false);
                setIdToDelete('');
            }
        }
    }

    const handleCreateInstance = async (empresaId) => {
        try {
            setLoading(true);
            const response = await createInstanceAdmin(empresaId); // V2: Sem userID

            if (response.status === 201) {
                toast.success(response.data.msg); // Exibe toast apenas se sucesso
                setRefresh(true); // Atualiza a lista
            }
        } catch (error) {
            process.env.REACT_APP_ISDEVELOP === 'true' && console.error("Erro ao criar instância:", error);
            const msg = error.response?.data?.msg || "Erro desconhecido";
            toast.error(msg);
        } finally {
            setLoading(false); // Garante que o estado de carregamento seja atualizado no final
        }
    };

    const handleRefresh = () => {
        setSearching(true);
        setRefresh(true);
    };

    const handlePageSizeChange = (newLimit) => {
        setLimit(parseInt(newLimit));
        setPage(1); // Resetar para primeira página
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const handleFilter = (e) => {
        setFilter(e.target.value);
        setPage(1); // Resetar para primeira página ao filtrar
    };

    // Troca qualquer filtro (situação/whatsapp/plano/datas) e volta pra primeira página
    const handleFilterChange = (setter) => (e) => {
        setter(e.target.value);
        setPage(1);
    };

    // Faturas sob demanda: abre o modal e busca as faturas no Asaas só nesse momento.
    const handleOpenFaturas = async (empresa) => {
        setIsModalOpen(true);
        setInvoicesLoading(true);
        setInvoices([]);
        try {
            const response = await getFaturasAdmin(empresa._id);
            setInvoices(response.data.faturas || []);
        } catch (error) {
            process.env.REACT_APP_ISDEVELOP === 'true' && console.error("Erro ao carregar faturas:", error);
            toast.error(t('common:adminEmpresas.invoices.loadError'));
            setInvoices([]);
        } finally {
            setInvoicesLoading(false);
        }
    }

    const handleOpenSubscription = (empresa) => {
        setSelectedEmpresaForSub(empresa);
        setSubscriptionModalOpen(true);
    };

    const handleDeleteMaster = (empresa) => {
        setEmpresaToDelete(empresa);
        setDeleteMasterModalOpen(true);
    }

    const handleDeleteMasterConfirm = async (usuario, senha) => {
        try {
            setDeleteMasterLoading(true);

            const response = await deleteEmpresaCompleta(empresaToDelete._id, usuario, senha);

            if (response.status === 200) {
                toast.success(`✅ ${response.data.msg}`);
                setDeleteMasterModalOpen(false);
                setEmpresaToDelete(null);
                setRefresh(true); // Atualiza a lista
            }
        } catch (error) {
            process.env.REACT_APP_ISDEVELOP === 'true' && console.error("Erro ao deletar empresa completa:", error);
            const errorMsg = error.response?.data?.msg || "Erro desconhecido ao deletar empresa";
            toast.error(`❌ ${errorMsg}`);
        } finally {
            setDeleteMasterLoading(false);
        }
    }

    const handleDeleteMasterClose = () => {
        setDeleteMasterModalOpen(false);
        setEmpresaToDelete(null);
    };

    // ---- Ativar / Inativar empresa ----
    const handleToggleActiveRequest = (empresa) => {
        setEmpresaToToggle(empresa);
        setToggleConfirmOpen(true);
    };

    const confirmToggleActive = async () => {
        if (!empresaToToggle) return;
        const target = empresaToToggle;
        const nextInativo = !target.inativo;
        try {
            setToggleLoading(true);
            await toggleEmpresaAtiva(target._id, nextInativo);
            toast.success(
                nextInativo
                    ? t('common:adminEmpresas.toggleActive.deactivatedSuccess')
                    : t('common:adminEmpresas.toggleActive.activatedSuccess')
            );
            // Update otimista (imutável) — reflete o badge na hora
            setEmpresas((prev) => prev.map((e) => (e._id === target._id ? { ...e, inativo: nextInativo } : e)));
            setToggleConfirmOpen(false);
            setEmpresaToToggle(null);
        } catch (error) {
            process.env.REACT_APP_ISDEVELOP === 'true' && console.error("Erro ao alternar status da empresa:", error);
            const msg = error.response?.data?.msg || t('common:adminEmpresas.toggleActive.error');
            toast.error(msg);
        } finally {
            setToggleLoading(false);
        }
    };

    const handleToggleClose = () => {
        setToggleConfirmOpen(false);
        setEmpresaToToggle(null);
    };

    // Função para gerar números de páginas para paginação
    const generatePageNumbers = () => {
        const totalPages = Math.ceil(total / limit);
        const pages = [];
        const maxVisiblePages = 5;

        let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage < maxVisiblePages - 1) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }

        return { pages, totalPages };
    };

    const { pages, totalPages } = generatePageNumbers();

    if (loading) {
        return (
            <PermissionGate permissions={['default']}>
                <AdminEmpresasContainer sidebar={sidebar}>
                    <AdminEmpresasContent>
                        <AdminEmpresasLoading>
                            <AiIcons.AiOutlineLoading3Quarters className="admin-empresas-loading-spinner" />
                            <div className="admin-empresas-loading-text">{t('common:adminEmpresas.loadingCompanies')}</div>
                        </AdminEmpresasLoading>
                    </AdminEmpresasContent>
                </AdminEmpresasContainer>
            </PermissionGate>
        );
    }

    return (
        <PermissionGate permissions={['default']}>
            <ConfirmDialog
                title={t('common:adminEmpresas.deleteInstance.title')}
                open={confirmOpen}
                setOpen={setConfirmOpen}
                onConfirm={deleteReg}
            >
                {t('common:adminEmpresas.deleteInstance.message')}
            </ConfirmDialog>

            <ConfirmDialog
                title={empresaToToggle?.inativo
                    ? t('common:adminEmpresas.toggleActive.activateTitle')
                    : t('common:adminEmpresas.toggleActive.deactivateTitle')}
                open={toggleConfirmOpen}
                setOpen={(v) => (v ? setToggleConfirmOpen(true) : handleToggleClose())}
                onConfirm={confirmToggleActive}
                loading={toggleLoading}
                confirmText={empresaToToggle?.inativo
                    ? t('common:adminEmpresas.toggleActive.activateConfirm')
                    : t('common:adminEmpresas.toggleActive.deactivateConfirm')}
                loadingText={t('common:adminEmpresas.toggleActive.processing')}
            >
                {empresaToToggle?.inativo
                    ? t('common:adminEmpresas.toggleActive.activateMessage', { name: empresaToToggle?.name })
                    : t('common:adminEmpresas.toggleActive.deactivateMessage', { name: empresaToToggle?.name })}
            </ConfirmDialog>

            <InvoicesModal
                invoices={invoices}
                loading={invoicesLoading}
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                t={t}
            />

            <DeleteMasterModal
                isOpen={deleteMasterModalOpen}
                onClose={handleDeleteMasterClose}
                onConfirm={handleDeleteMasterConfirm}
                empresaData={empresaToDelete}
                isLoading={deleteMasterLoading}
                t={t}
            />

            <ModalSubscriptionAdmin
                isOpen={subscriptionModalOpen}
                onClose={() => {
                    setSubscriptionModalOpen(false);
                    setSelectedEmpresaForSub(null);
                }}
                empresa={selectedEmpresaForSub}
                onUpdate={() => setRefresh(true)}
            />

            <AdminEmpresasContainer sidebar={sidebar}>
                <AdminEmpresasContent>
                    <AdminEmpresasHeader>
                        <span className="admin-empresas-header-accent" />
                        <div>
                            <h1 className="admin-empresas-title">{t('common:adminEmpresas.title')}</h1>
                            <p className="admin-empresas-subtitle">
                                {t('common:adminEmpresas.subtitle')}
                            </p>
                        </div>
                    </AdminEmpresasHeader>

                    <AdminEmpresasControls>
                        <div className="admin-empresas-controls-left">
                            <div className="admin-empresas-search-container">
                                <FiIcons.FiSearch className="admin-empresas-search-icon" />
                                <input
                                    className="admin-empresas-search-input"
                                    placeholder={t('common:adminEmpresas.searchPlaceholder')}
                                    value={filter}
                                    onChange={handleFilter}
                                />
                            </div>
                            <div className="admin-empresas-filter">
                                <FiIcons.FiFilter className="admin-empresas-filter-icon" />
                                <select
                                    className="admin-empresas-filter-select"
                                    value={situacaoFilter}
                                    onChange={handleFilterChange(setSituacaoFilter)}
                                    aria-label={t('common:adminEmpresas.columns.activeStatus')}
                                >
                                    <option value="ativa">{t('common:adminEmpresas.filters.active')}</option>
                                    <option value="inativa">{t('common:adminEmpresas.filters.inactive')}</option>
                                    <option value="todas">{t('common:adminEmpresas.filters.all')}</option>
                                </select>
                            </div>
                            <div className="admin-empresas-filter">
                                <FiIcons.FiSmartphone className="admin-empresas-filter-icon" />
                                <select
                                    className="admin-empresas-filter-select"
                                    value={whatsappFilter}
                                    onChange={handleFilterChange(setWhatsappFilter)}
                                    aria-label={t('common:adminEmpresas.columns.whatsappStatus')}
                                >
                                    <option value="todas">{t('common:adminEmpresas.filters.whatsappAll')}</option>
                                    <option value="conectado">{t('common:adminEmpresas.status.connected')}</option>
                                    <option value="desconectado">{t('common:adminEmpresas.status.disconnected')}</option>
                                    <option value="sem_instancia">{t('common:adminEmpresas.filters.noInstance')}</option>
                                </select>
                            </div>
                            <div className="admin-empresas-filter">
                                <FiIcons.FiCreditCard className="admin-empresas-filter-icon" />
                                <select
                                    className="admin-empresas-filter-select"
                                    value={planoFilter}
                                    onChange={handleFilterChange(setPlanoFilter)}
                                    aria-label={t('common:adminEmpresas.columns.planStatus')}
                                >
                                    <option value="todos">{t('common:adminEmpresas.filters.planAll')}</option>
                                    <option value="ativo">{t('common:adminEmpresas.filters.planActive')}</option>
                                    <option value="trial">{t('common:adminEmpresas.filters.planTrial')}</option>
                                    <option value="inativo">{t('common:adminEmpresas.filters.planInactive')}</option>
                                    <option value="suspenso">{t('common:adminEmpresas.filters.planSuspended')}</option>
                                </select>
                            </div>
                            <div className="admin-empresas-daterange" title={t('common:adminEmpresas.filters.createdPeriod')}>
                                <FiIcons.FiCalendar className="admin-empresas-daterange-icon" />
                                <input
                                    type="date"
                                    className="admin-empresas-date-input"
                                    value={dataInicio}
                                    onChange={handleFilterChange(setDataInicio)}
                                    aria-label={t('common:adminEmpresas.filters.dateFrom')}
                                />
                                <span className="admin-empresas-daterange-sep">–</span>
                                <input
                                    type="date"
                                    className="admin-empresas-date-input"
                                    value={dataFim}
                                    onChange={handleFilterChange(setDataFim)}
                                    aria-label={t('common:adminEmpresas.filters.dateTo')}
                                />
                            </div>
                        </div>
                        <button
                            className="admin-empresas-refresh-btn"
                            onClick={handleRefresh}
                            disabled={loading}
                        >
                            <SlIcons.SlRefresh />
                            {t('common:buttons.refresh')}
                        </button>
                    </AdminEmpresasControls>

                    <AdminEmpresasTable>
                        {searching && (
                            <div className="admin-empresas-table-overlay">
                                <AiIcons.AiOutlineLoading3Quarters className="admin-empresas-loading-spinner" />
                            </div>
                        )}
                        <div className="admin-empresas-table-wrapper" style={{ opacity: searching ? 0.5 : 1, transition: 'opacity 0.2s' }}>
                            <table className="admin-empresas-table">
                                <thead className="admin-empresas-table-header">
                                    <tr>
                                        <th>{t('common:adminEmpresas.columns.name')}</th>
                                        <th>{t('common:adminEmpresas.columns.email')}</th>
                                        <th>{t('common:adminEmpresas.columns.cpfCnpj')}</th>
                                        <th>{t('common:adminEmpresas.columns.instanceId')}</th>
                                        <th>{t('common:adminEmpresas.columns.whatsappStatus')}</th>
                                        <th>{t('common:adminEmpresas.columns.activeStatus')}</th>
                                        <th>{t('common:adminEmpresas.columns.createdAt')}</th>
                                        {showSubscriptionColumn && (
                                            <>
                                                <th>{t('common:adminEmpresas.columns.planStatus', 'Plan Status')}</th>
                                                <th>{t('common:adminEmpresas.columns.expiry', 'Expiry')}</th>
                                            </>
                                        )}
                                        <th>{t('common:adminEmpresas.columns.viewInvoices')}</th>
                                        <th>{t('common:adminEmpresas.columns.actions')}</th>
                                    </tr>
                                </thead>
                                <tbody className="admin-empresas-table-body">
                                    {empresas.map((empresa) => (
                                        <tr key={empresa._id}>
                                            <td>{empresa.name}</td>
                                            <td className="admin-empresas-cell-email">{empresa.email}</td>
                                            <td className="admin-empresas-cell-mono">{empresa.cnpj}</td>
                                            <td>
                                                <span className="admin-empresas-cell-instance" title={empresa.whatsapp?.id || "N/A"}>
                                                    {empresa.whatsapp?.id || "N/A"}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`admin-empresas-status ${
                                                    empresa.instanceStatus?.code === 400 ? 'disconnected'
                                                        : empresa.instanceStatus?.connected ? 'connected'
                                                            : 'unknown'
                                                }`}>
                                                    <span className="dot" />
                                                    {empresa.instanceStatus?.code === 400 ? t('common:adminEmpresas.status.disconnected')
                                                        : empresa.instanceStatus?.connected ? t('common:adminEmpresas.status.connected')
                                                            : t('common:adminEmpresas.status.unknown')}
                                                </span>
                                            </td>
                                            <td>
                                                <StatusBadge inativo={empresa.inativo} t={t} />
                                            </td>
                                            <td className="admin-empresas-cell-mono">{moment(empresa.createdAt).format("DD/MM/YYYY")}</td>
                                            {showSubscriptionColumn && (
                                                <>
                                                    <td>
                                                        <span className={`admin-empresas-status ${
                                                            empresa.status_plano === 'ativo' ? 'connected'
                                                                : empresa.status_plano === 'trial' ? 'unknown'
                                                                : 'disconnected'
                                                        }`}>
                                                            <span className="dot" />
                                                            {empresa.status_plano || 'trial'}
                                                        </span>
                                                    </td>
                                                    <td className="admin-empresas-cell-mono">
                                                        {empresa.data_vencimento_licenca
                                                            ? moment(empresa.data_vencimento_licenca).format("DD/MM/YYYY")
                                                            : '-'}
                                                    </td>
                                                </>
                                            )}
                                            <td>
                                                {empresa.hasFaturas ? (
                                                    <span
                                                        className="admin-empresas-faturas-link"
                                                        onClick={() => handleOpenFaturas(empresa)}
                                                    >
                                                        {t('common:adminEmpresas.invoices.viewInvoices')}
                                                    </span>
                                                ) : (
                                                    <span className="admin-empresas-faturas-empty">{t('common:adminEmpresas.invoices.notGenerated')}</span>
                                                )}
                                            </td>
                                            <td>
                                                <EmpresaActionsMenu
                                                    empresa={empresa}
                                                    showSubscription={showSubscriptionColumn}
                                                    onViewInvoices={() => handleOpenFaturas(empresa)}
                                                    onManageSubscription={() => handleOpenSubscription(empresa)}
                                                    onCreateInstance={() => handleCreateInstance(empresa._id)}
                                                    onDeleteInstance={() => handleDelete(empresa._id)}
                                                    onToggleActive={() => handleToggleActiveRequest(empresa)}
                                                    onDeleteMaster={() => handleDeleteMaster({
                                                        _id: empresa._id,
                                                        name: empresa.name,
                                                        email: empresa.email,
                                                        cnpj: empresa.cnpj
                                                    })}
                                                    t={t}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <AdminEmpresasPagination>
                            <div className="admin-empresas-pagination-info">
                                {t('common:adminEmpresas.showing', { from: Math.min(((page - 1) * limit) + 1, total), to: Math.min(page * limit, total) })}{' '}
                                <span className="admin-empresas-total-highlight">{total}</span> {t('common:adminEmpresas.companies')}
                            </div>

                            <div className="admin-empresas-pagination-controls">
                                <div className="admin-empresas-page-size-selector">
                                    <label>{t('common:pagination.itemsPerPage')}:</label>
                                    <select
                                        value={limit}
                                        onChange={(e) => handlePageSizeChange(e.target.value)}
                                    >
                                        <option value={5}>5</option>
                                        <option value={10}>10</option>
                                        <option value={20}>20</option>
                                        <option value={50}>50</option>
                                        <option value={100}>100</option>
                                    </select>
                                </div>

                                <div className="admin-empresas-page-navigation">
                                    <button
                                        className="admin-empresas-page-btn"
                                        onClick={() => handlePageChange(page - 1)}
                                        disabled={page === 1}
                                    >
                                        <FiIcons.FiChevronLeft />
                                    </button>

                                    {pages.map((pageNumber) => (
                                        <button
                                            key={pageNumber}
                                            className={`admin-empresas-page-btn ${pageNumber === page ? 'active' : ''}`}
                                            onClick={() => handlePageChange(pageNumber)}
                                        >
                                            {pageNumber}
                                        </button>
                                    ))}

                                    <button
                                        className="admin-empresas-page-btn"
                                        onClick={() => handlePageChange(page + 1)}
                                        disabled={page === totalPages}
                                    >
                                        <FiIcons.FiChevronRight />
                                    </button>
                                </div>
                            </div>
                        </AdminEmpresasPagination>
                    </AdminEmpresasTable>
                </AdminEmpresasContent>
            </AdminEmpresasContainer>
        </PermissionGate>
    );
};

export default ListEmpresas;
